import {
  MyLeadsSearchQuery,
  MyLeadsFilterTypes,
} from '@mm/state/interfaces/myLeads';
import { DatePicker } from 'antd';
import { RangePickerProps } from 'antd/es/date-picker';
import classNames from 'classnames';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { useFilterParams } from '../use-filter-params';
import styles from './DatesPicker.module.css';

type Props = {
  filterParams: MyLeadsSearchQuery;
  setFilterParams: (params: MyLeadsSearchQuery) => void;
  className?: string;
};

export const DatesPicker = ({
  filterParams,
  setFilterParams,
  className,
}: Props) => {
  const KEY = 'created_at';
  const defaultStartDate =
    filterParams?.query?.attributes?.created_at?.[0].start_date;
  const defaultEndDate =
    filterParams?.query?.attributes?.created_at?.[0].end_date;

  const [startDate, setStartDate] = useState(defaultStartDate);
  const [endDate, setEndDate] = useState(defaultEndDate);
  const { getUpdatedFilterParams } = useFilterParams({
    filterParams,
  });

  const disabledStartDate: RangePickerProps['disabledDate'] = current =>
    current > dayjs(endDate).endOf('day');
  const disabledEndDate: RangePickerProps['disabledDate'] = current =>
    current < dayjs(startDate).startOf('day');

  const onDatesChange = (dateString: string, isStartDate: boolean) => {
    const startDate =
      isStartDate && dateString
        ? dateString
        : isStartDate && !dateString
        ? undefined
        : defaultStartDate;

    const endDate =
      !isStartDate && dateString
        ? dateString
        : !isStartDate && !dateString
        ? undefined
        : defaultEndDate;

    delete filterParams.query?.attributes?.created_at;

    const updatedQuery = getUpdatedFilterParams({
      key: KEY,
      value: { start_date: startDate, end_date: endDate },
      isExisting: false,
      type: MyLeadsFilterTypes.attributes,
    });
    setFilterParams(updatedQuery);
  };

  useEffect(() => {
    if (!defaultStartDate) {
      setStartDate('');
    }
    if (!defaultEndDate) {
      setEndDate('');
    }
    if (!defaultStartDate && !defaultEndDate) {
      delete filterParams.query?.attributes?.created_at;

      if (!Object.keys(filterParams?.query?.attributes || {}).length) {
        delete filterParams.query?.attributes;
      }
    }
  }, [defaultStartDate, defaultEndDate, filterParams.query?.attributes]);

  return (
    <div className={classNames(styles.container, className)}>
      <DatePicker
        name="startDate"
        value={startDate ? dayjs(startDate || '') : undefined}
        onChange={(_, startDate) => {
          if (typeof startDate === 'string') {
            setStartDate(startDate);
            onDatesChange(startDate, true);
          }
        }}
        disabledDate={disabledStartDate}
        getPopupContainer={triggerNode =>
          triggerNode.parentElement || document.body
        }
      />
      <DatePicker
        name="endDate"
        value={endDate ? dayjs(endDate || '') : undefined}
        onChange={(_, endDate) => {
          if (typeof endDate === 'string') {
            setEndDate(endDate);
            onDatesChange(endDate, false);
          }
        }}
        disabledDate={disabledEndDate}
        getPopupContainer={triggerNode =>
          triggerNode.parentElement || document.body
        }
      />
    </div>
  );
};
